import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShoppingBag, ArrowRight, Loader2, DollarSign, RefreshCw, Heart, ShoppingCart, Bookmark } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ClientServicesPage = () => {
  const { getAuthHeaders } = useAuth();
  const [services, setServices] = useState([]);
  const [rates, setRates] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orderModal, setOrderModal] = useState(null);
  const [orderForm, setOrderForm] = useState({ details: '', amount: '', payment_method_id: '' });
  const [submitting, setSubmitting] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [addingToCart, setAddingToCart] = useState(null);

  // Currency converter state
  const [convertFrom, setConvertFrom] = useState('USD');
  const [convertAmount, setConvertAmount] = useState('');
  const [convertResult, setConvertResult] = useState(null);

  const load = async () => {
    try {
      const h = { headers: getAuthHeaders() };
      const [s, r, pm, w] = await Promise.all([
        axios.get(`${API}/client/services`, h),
        axios.get(`${API}/client/exchange-rates`, h),
        axios.get(`${API}/client/payment-methods`, h),
        axios.get(`${API}/client/wallet/summary`, h),
      ]);
      setServices(s.data);
      setRates(r.data);
      setPaymentMethods(pm.data);
      setWallet(w.data);
      // Load favorites
      try {
        const cl = await axios.get(`${API}/client/favorites`, h);
        setFavorites(cl.data.map(f => f.id));
      } catch (e) { /* ignore */ }
      // Load wishlist
      try {
        const wl = await axios.get(`${API}/client/wishlist`, h);
        setWishlist(wl.data.map(w => w.service_id));
      } catch (e) { /* ignore */ }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleConvert = () => {
    if (!convertAmount || !rates.length) return;
    const rate = rates.find(r => r.from_currency === convertFrom);
    if (rate) {
      setConvertResult({
        from: convertFrom,
        to: rate.to_currency,
        amount: parseFloat(convertAmount),
        result: parseFloat(convertAmount) * rate.rate,
        rate: rate.rate
      });
    }
  };

  const toggleFavorite = async (serviceId) => {
    try {
      await axios.post(`${API}/client/favorites/${serviceId}`, {}, { headers: getAuthHeaders() });
      setFavorites(prev => prev.includes(serviceId) ? prev.filter(f => f !== serviceId) : [...prev, serviceId]);
    } catch (e) { toast.error('Error'); }
  };

  const addToCart = async (service) => {
    setAddingToCart(service.id);
    try {
      await axios.post(`${API}/client/cart/add`, { service_id: service.id, quantity: 1 }, { headers: getAuthHeaders() });
      toast.success(`${service.name} agregado al carrito`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
    finally { setAddingToCart(null); }
  };

  const toggleWishlist = async (serviceId) => {
    try {
      const res = await axios.post(`${API}/client/wishlist/${serviceId}`, {}, { headers: getAuthHeaders() });
      setWishlist(prev => prev.includes(serviceId) ? prev.filter(f => f !== serviceId) : [...prev, serviceId]);
      toast.success(res.data.message);
    } catch (e) { toast.error('Error'); }
  };

  const handleOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/client/orders`, {
        service_type: orderModal.name,
        details: { notes: orderForm.details },
        payment_method_id: orderForm.payment_method_id || paymentMethods[0]?.id,
        amount: parseFloat(orderForm.amount)
      }, { headers: getAuthHeaders() });
      toast.success('Orden creada exitosamente');
      setOrderModal(null);
      setOrderForm({ details: '', amount: '', payment_method_id: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear orden');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" /></div>;

  const categoryIcons = { vuelos: '✈', remesas: '💸', recargas: '📱' };

  return (
    <div className="space-y-6" data-testid="client-services-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Servicios</h1>
        <p className="text-slate-500">Explora nuestros servicios disponibles</p>
      </div>

      {/* Currency Converter */}
      {rates.length > 0 && (
        <Card className="p-5 border-0 shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50" data-testid="currency-converter">
          <div className="flex items-center gap-2 mb-3">
            <RefreshCw className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Convertidor de Moneda</h3>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Moneda</Label>
              <select value={convertFrom} onChange={(e) => setConvertFrom(e.target.value)} className="h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="convert-from-select">
                {[...new Set(rates.map(r => r.from_currency))].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1 flex-1 min-w-[120px]">
              <Label className="text-xs">Monto</Label>
              <Input type="number" step="0.01" value={convertAmount} onChange={(e) => setConvertAmount(e.target.value)} placeholder="100" data-testid="convert-amount" />
            </div>
            <Button onClick={handleConvert} className="bg-blue-600 hover:bg-blue-700" data-testid="convert-btn">
              Convertir
            </Button>
            {convertResult && (
              <div className="w-full p-3 bg-white rounded-lg border text-sm" data-testid="convert-result">
                <p><strong>{convertResult.amount} {convertResult.from}</strong> = <strong className="text-blue-700">{convertResult.result.toFixed(2)} {convertResult.to}</strong></p>
                <p className="text-xs text-slate-500">Tasa: 1 {convertResult.from} = {convertResult.rate} {convertResult.to}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Balance info */}
      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-emerald-600" />
        <span className="text-sm text-emerald-700 font-medium">Tu saldo: ${(wallet?.balance || 0).toFixed(2)} USD</span>
      </div>

      {/* Services Grid */}
      {services.length === 0 ? (
        <Card className="p-12 text-center border-0 shadow-sm">
          <ShoppingBag className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">No hay servicios disponibles por el momento</p>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map((s) => (
            <Card key={s.id} className="p-5 border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`service-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div className="text-3xl mb-3">{categoryIcons[s.category] || '📦'}</div>
                <button onClick={() => toggleFavorite(s.id)} className="p-1" data-testid={`fav-toggle-${s.id}`}>
                  <Heart className={`w-5 h-5 transition-colors ${favorites.includes(s.id) ? 'fill-red-500 text-red-500' : 'text-slate-300 hover:text-red-400'}`} />
                </button>
              </div>
              <h3 className="font-semibold text-lg text-slate-900">{s.name}</h3>
              <p className="text-sm text-slate-500 mt-1">{s.description || s.category}</p>
              {s.currency && <p className="text-xs text-slate-400 mt-1">Moneda: {s.currency}</p>}
              <div className="flex gap-2 mt-4">
                <Button onClick={() => addToCart(s)} disabled={addingToCart === s.id} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid={`cart-btn-${s.id}`}>
                  {addingToCart === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ShoppingCart className="w-4 h-4 mr-1" /> Al carrito</>}
                </Button>
                <Button variant="outline" size="icon" onClick={() => toggleWishlist(s.id)} data-testid={`wish-btn-${s.id}`}>
                  <Bookmark className={`w-4 h-4 ${wishlist.includes(s.id) ? 'fill-blue-500 text-blue-500' : 'text-slate-400'}`} />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Order Modal */}
      {orderModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="order-modal">
          <Card className="w-full max-w-md p-6">
            <h3 className="text-lg font-semibold mb-4">Solicitar: {orderModal.name}</h3>
            <form onSubmit={handleOrder} className="space-y-4">
              <div className="space-y-2">
                <Label>Monto (USD)</Label>
                <Input type="number" step="0.01" min="0.01" value={orderForm.amount} onChange={(e) => setOrderForm({ ...orderForm, amount: e.target.value })} required data-testid="order-amount" />
              </div>
              <div className="space-y-2">
                <Label>Detalles del servicio</Label>
                <textarea value={orderForm.details} onChange={(e) => setOrderForm({ ...orderForm, details: e.target.value })} className="w-full min-h-[80px] p-3 rounded-md border border-slate-300 text-sm" placeholder="Describe lo que necesitas..." data-testid="order-details" />
              </div>
              {paymentMethods.length > 0 && (
                <div className="space-y-2">
                  <Label>Metodo de pago</Label>
                  <select value={orderForm.payment_method_id} onChange={(e) => setOrderForm({ ...orderForm, payment_method_id: e.target.value })} className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm" data-testid="order-method-select">
                    {paymentMethods.map((pm) => (
                      <option key={pm.id} value={pm.id}>{pm.bank_name} ({pm.country})</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex gap-3">
                <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 flex-1" data-testid="order-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Orden'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setOrderModal(null)}>Cancelar</Button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ClientServicesPage;
